import { Controller } from "@hotwired/stimulus";

export default class FlashController extends Controller {

  connect() {
    this.translate();
  }

  async translate() {
    this.active = true;
    const isBackForward = performance.getEntriesByType("navigation")[0]?.type === "back_forward";
    if (isBackForward) return;

    const message = this.element.dataset.flashMessage;
    const type = this.element.dataset.flashType || "success";
    if (!message) return;

    const translatedMessage = await this.translateMessage(message);
    if (!this.active) return;

    await this.showAlert(translatedMessage, type);
    if (this.active) this.element.remove();
  }

  async translateMessage(message) {
    try {
      // Translate text nodes only, preserving markup and escaping translated text.
      const source = document.createElement("div");
      source.innerHTML = message;
      const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) {
        if (walker.currentNode.textContent.trim()) nodes.push(walker.currentNode);
      }
      if (!nodes.length) return message;

      const texts = nodes.map(node => node.textContent);
      const translations = await Promise.race([
        this.waitForTranslator().then(translator => translator?.translateInterfaceTexts(texts) || texts),
        new Promise(resolve => {
          this.translationFallback = globalThis.setTimeout(() => resolve(texts), 1500);
          this.cancelTranslation = () => resolve(texts);
        })
      ]);
      for (const [index, node] of nodes.entries()) {
        const translated = translations?.[index];
        if (typeof translated === "string" && translated.trim()) node.textContent = translated;
      }
      return source.innerHTML;
    } catch {
      return message;
    } finally {
      globalThis.clearTimeout(this.translationFallback);
      this.cancelTranslation = null;
      this.cancelTranslatorWait?.();
    }
  }

  waitForTranslator() {
    const element = document.getElementById("google-translate-block");
    const translator = element && this.application.getControllerForElementAndIdentifier(
      element, "google-translate"
    );
    if (translator?.translateInterfaceTexts) return Promise.resolve(translator);
    if (!element) return Promise.resolve(null);

    // Redirect flashes can connect before the translator farther down the page.
    // Share the existing 1.5-second deadline rather than showing English early.
    return new Promise(resolve => {
      const ready = event => {
        cleanup();
        resolve(event.detail.controller);
      };
      const cleanup = () => {
        document.removeEventListener("google-translate:ready", ready);
        this.cancelTranslatorWait = null;
      };
      this.cancelTranslatorWait = () => {
        cleanup();
        resolve(null);
      };
      document.addEventListener("google-translate:ready", ready);
    });
  }

  showAlert(message, type) {
    return Swal.fire({
      customClass: {
        popup: "swal-popup alert-sm-bg align-items-center justify-content-center w-100",
        htmlContainer: "text-black-paint text-white notranslate"
      },
      animation: false,
      html: message,
      icon: type,
      showCloseButton: true,
      buttonsStyling: false,
      showConfirmButton: false,
      timer: 4000,
      allowEscapeKey: true,
      allowOutsideClick: true
    });
  }

  disconnect() {
    this.active = false;
    this.cancelTranslation?.();
    this.cancelTranslatorWait?.();
    globalThis.clearTimeout(this.translationFallback);
  }
}
